import React, { useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import {
  Upload,
  FileText,
  X,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Info,
  Search,
  RefreshCw,
  Download,
  ExternalLink
} from 'lucide-react'
import { searchApi } from '../services/api'
import SniffingDog from '../components/common/SniffingDog'

export default function EngineerSearch() {
  const [uploadedFile, setUploadedFile] = useState(null)
  const [searchResults, setSearchResults] = useState(null)

  // PDF upload search mutation
  const uploadMutation = useMutation({
    mutationFn: (file) => searchApi.searchByUpload(file, {
      min_similarity: 0.3,
      max_results: 10
    }),
    onSuccess: (response) => {
      setSearchResults(response.data)
    }
  })

  // File drop handler
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0]
    if (file) {
      setUploadedFile(file)
      uploadMutation.mutate(file)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    noClick: false,
    noKeyboard: false
  })

  const clearSearch = () => {
    setUploadedFile(null)
    setSearchResults(null)
    uploadMutation.reset()
  }

  const retrySearch = () => {
    if (uploadedFile) {
      uploadMutation.mutate(uploadedFile)
    }
  }

  const isSearching = uploadMutation.isPending

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center pb-2">
        <h1 className="text-2xl font-bold text-gray-900">Search Documents with AI</h1>
        <p className="text-gray-500 mt-1">
          Upload a PDF to automatically extract specifications and find similar documents
        </p>
      </div>

      {/* PDF Upload Section */}
      <div className="card">
        <div className="card-body">
          {!uploadedFile ? (
            /* Empty State - Drag & Drop Zone */
            <div
              {...getRootProps()}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                ${isDragActive
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                }`}
            >
              <input {...getInputProps()} />

              <div className="flex flex-col items-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors
                  ${isDragActive ? 'bg-primary-100' : 'bg-gray-100'}`}>
                  <Upload className={`w-8 h-8 ${isDragActive ? 'text-primary-600' : 'text-gray-400'}`} />
                </div>

                <p className="text-lg font-medium text-gray-700 mb-1">
                  {isDragActive ? 'Drop the PDF here' : 'Drag & drop a file to search in Master Folder'}
                </p>
                <p className="text-sm text-gray-500 mb-4">or click anywhere to browse</p>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={(e) => { e.stopPropagation(); open(); }}
                >
                  <Search className="w-4 h-4 mr-2" />
                  Select File to Search
                </button>

                <p className="text-xs text-gray-400 mt-4">
                  Supported format: PDF files only (max 50MB)
                </p>
              </div>
            </div>
          ) : (
            /* File Uploaded State */
            <div className="space-y-4">
              {/* File Info Card */}
              <div className={`flex items-center justify-between p-4 rounded-lg border transition-colors
                ${isSearching ? 'bg-primary-50 border-primary-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center flex-1 min-w-0">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4
                    ${isSearching ? 'bg-primary-100' : 'bg-white border border-gray-200'}`}>
                    <FileText className={`w-6 h-6 ${isSearching ? 'text-primary-600' : 'text-gray-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{uploadedFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(uploadedFile.size / 1024).toFixed(1)} KB
                      {isSearching && (
                        <span className="ml-2 text-primary-600">
                          • Extracting specifications...
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {!isSearching && searchResults && (
                    <button
                      onClick={retrySearch}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Search again"
                    >
                      <RefreshCw className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={clearSearch}
                    disabled={isSearching}
                    className="p-2 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Remove file"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Processing Indicator */}
              {isSearching && (
                <div className="flex flex-col items-center justify-center p-8 bg-gradient-to-br from-primary-50 to-accent-purple/30 rounded-xl border-2 border-dashed border-primary-200">
                  <SniffingDog size={150} />
                </div>
              )}

              {/* Error State */}
              {uploadMutation.isError && (
                <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-danger-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-danger-800">Search Failed</p>
                      <p className="text-sm text-danger-700 mt-1">
                        {uploadMutation.error?.response?.data?.detail || uploadMutation.error?.message || 'An error occurred while processing the PDF'}
                      </p>
                      <button
                        onClick={retrySearch}
                        className="mt-2 text-sm font-medium text-danger-700 hover:text-danger-800 underline"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Search Results */}
      {searchResults && !isSearching && (
        <div className="space-y-6">
          {/* Score Explanation */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-blue-800">
                <strong>Understanding scores:</strong> <strong>Similarity %</strong> shows how closely a stored specification matches your search. <strong>Data Quality %</strong> indicates how reliably data was extracted from that specification's PDF (incomplete extraction = lower quality).
              </div>
            </div>
          </div>

          {/* Query Summary */}
          <div className="card bg-primary-50 border-primary-200">
            <div className="card-body">
              <h3 className="font-medium text-primary-900 mb-3">Extracted Query Data</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {searchResults.query_data?.dimensions && (
                  <div>
                    <span className="text-primary-600">Dimensions:</span>
                    <p className="font-medium">
                      {searchResults.query_data.dimensions.width || '?'} ×{' '}
                      {searchResults.query_data.dimensions.height || '?'} ×{' '}
                      {searchResults.query_data.dimensions.thickness || '?'} mm
                    </p>
                  </div>
                )}
                {searchResults.query_data?.material?.alloy_temper && (
                  <div>
                    <span className="text-primary-600">Alloy:</span>
                    <p className="font-medium">{searchResults.query_data.material.alloy_temper}</p>
                  </div>
                )}
                {searchResults.query_data?.profile_type && (
                  <div>
                    <span className="text-primary-600">Profile:</span>
                    <p className="font-medium capitalize">{searchResults.query_data.profile_type}</p>
                  </div>
                )}
                <div>
                  <span className="text-primary-600">Search Time:</span>
                  <p className="font-medium">{searchResults.search_duration_ms} ms</p>
                </div>
              </div>

              {searchResults.query_data?.extraction_warnings?.length > 0 && (
                <div className="mt-4 p-3 bg-warning-50 rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-warning-600 mr-2 mt-0.5" />
                    <div>
                      <p className="font-medium text-warning-800">Extraction Warnings:</p>
                      <ul className="text-sm text-warning-700 mt-1">
                        {searchResults.query_data.extraction_warnings.map((w, i) => (
                          <li key={i}>• {w}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Summary */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {searchResults.total_matches} Matches Found
              </h2>
              <p className="text-gray-500">
                {searchResults.high_confidence_matches} high confidence •{' '}
                {searchResults.same_alloy_matches} same alloy
              </p>
            </div>
            {searchResults.best_match_score && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Best Match</p>
                <p className={`text-2xl font-bold ${getScoreColor(searchResults.best_match_score)}`}>
                  {(searchResults.best_match_score * 100).toFixed(0)}%
                </p>
              </div>
            )}
          </div>

          {/* Match Cards */}
          {searchResults.matches?.length > 0 ? (
            <div className="space-y-4">
              {searchResults.matches.map((match) => (
                <MatchCard key={match.id} match={match} />
              ))}
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-center py-12">
                <Info className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-700">No matches found</p>
                <p className="text-sm text-gray-500 mt-1">
                  Try uploading a different PDF or check if the repository has been processed
                </p>
              </div>
            </div>
          )}

          {/* Suggestions */}
          {searchResults.search_suggestions?.length > 0 && (
            <div className="card bg-blue-50 border-blue-200">
              <div className="card-body">
                <h4 className="font-medium text-blue-900 mb-2">Suggestions</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  {searchResults.search_suggestions.map((s, i) => (
                    <li key={i}>• {s}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function MatchCard({ match }) {
  const [expanded, setExpanded] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const filePath = (match.file_path || '').trim()
  // Support both Azure and DigitalOcean Spaces cloud storage paths
  const filePathLower = filePath.toLowerCase()
  const isCloudPath = filePathLower.startsWith('azure://') || filePathLower.startsWith('spaces://') || filePathLower.includes('spaces://') || filePathLower.includes('azure://')

  // Handle PDF download
  const handleDownload = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!isCloudPath || downloading) return

    setDownloading(true)
    try {
      const response = await searchApi.getDownloadUrl(filePath)
      const downloadUrl = response.data.download_url

      // Open in new tab
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error('Failed to get download URL:', error)
      alert('Failed to download PDF. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="card-body">
        <div className="flex items-start justify-between">
          {/* Main Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-bold text-gray-900">{match.section_number}</h3>
              <span className="badge badge-gray">Rank #{match.rank}</span>
              {/* File Path - clickable for cloud storage paths (Azure/Spaces) */}
              {isCloudPath ? (
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="inline-flex items-center gap-1.5 text-sm bg-blue-100 text-blue-700 px-3 py-1.5 rounded-md hover:bg-blue-200 hover:text-blue-900 transition-colors cursor-pointer border border-blue-300 shadow-sm"
                  title={`Click to download: ${filePath}`}
                >
                  {downloading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="font-semibold">Download PDF</span>
                </button>
              ) : filePath ? (
                <span className="inline-flex items-center text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-md border border-gray-300" title={filePath}>
                  <FileText className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500" />
                  <span className="font-medium truncate max-w-xs md:max-w-md lg:max-w-lg">
                    {filePath}
                  </span>
                </span>
              ) : null}
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Dimensions:</span>
                <p className="font-medium">
                  {match.dimensions?.width || '?'} × {match.dimensions?.height || '?'} × {match.dimensions?.thickness || '?'} mm
                </p>
              </div>
              <div>
                <span className="text-gray-500">Alloy:</span>
                <p className="font-medium">{match.material?.alloy_temper || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Profile:</span>
                <p className="font-medium capitalize">{match.profile_type || 'Unknown'}</p>
              </div>
              <div>
                <span className="text-gray-500" title="How reliably data was extracted from this specification's PDF">Data Quality:</span>
                <p className="font-medium">{(match.extraction_confidence * 100).toFixed(0)}%</p>
              </div>
            </div>

            {/* Match Reasons */}
            {match.match_reasons?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {match.match_reasons.map((reason, i) => (
                  <span key={i} className="badge badge-success">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {reason}
                  </span>
                ))}
              </div>
            )}

            {/* Mismatch Warnings */}
            {match.mismatch_warnings?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {match.mismatch_warnings.slice(0, expanded ? undefined : 2).map((warning, i) => (
                  <span key={i} className="badge badge-warning">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {warning}
                  </span>
                ))}
                {!expanded && match.mismatch_warnings.length > 2 && (
                  <span className="text-sm text-gray-500">
                    +{match.mismatch_warnings.length - 2} more
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Score */}
          <div className="text-center ml-6" title="How similar this specification is to your uploaded PDF">
            <div className={`text-3xl font-bold ${getScoreColor(match.overall_score)}`}>
              {(match.overall_score * 100).toFixed(0)}%
            </div>
            <p className="text-xs text-gray-500">Similarity</p>
          </div>
        </div>

        {/* Expand/Collapse */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-4 flex items-center text-sm text-primary-600 hover:text-primary-800"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4 mr-1" />
              Hide Details
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-1" />
              Show Score Breakdown
            </>
          )}
        </button>

        {/* Expanded Details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Score Breakdown</h4>
            <div className="space-y-3">
              <ScoreBar
                label="Dimensions"
                score={match.score_breakdown?.dimensions_score}
                weight={match.score_breakdown?.dimensions_weight}
              />
              <ScoreBar
                label="Alloy/Temper"
                score={match.score_breakdown?.alloy_score}
                weight={match.score_breakdown?.alloy_weight}
              />
              <ScoreBar
                label="Profile Type"
                score={match.score_breakdown?.profile_type_score}
                weight={match.score_breakdown?.profile_type_weight}
              />
              <ScoreBar
                label="Technical Params"
                score={match.score_breakdown?.technical_score}
                weight={match.score_breakdown?.technical_weight}
              />
            </div>

            {/* Dimension Differences */}
            {match.dimension_diff && Object.keys(match.dimension_diff).length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">Dimension Differences</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  {match.dimension_diff.width_diff_mm !== undefined && (
                    <div>
                      <span className="text-gray-500">Width diff:</span>
                      <p className="font-medium">
                        {match.dimension_diff.width_diff_mm.toFixed(1)} mm
                        ({match.dimension_diff.width_diff_pct?.toFixed(1)}%)
                      </p>
                    </div>
                  )}
                  {match.dimension_diff.height_diff_mm !== undefined && (
                    <div>
                      <span className="text-gray-500">Height diff:</span>
                      <p className="font-medium">
                        {match.dimension_diff.height_diff_mm.toFixed(1)} mm
                        ({match.dimension_diff.height_diff_pct?.toFixed(1)}%)
                      </p>
                    </div>
                  )}
                  {match.dimension_diff.thickness_diff_mm !== undefined && (
                    <div>
                      <span className="text-gray-500">Thickness diff:</span>
                      <p className="font-medium">
                        {match.dimension_diff.thickness_diff_mm.toFixed(1)} mm
                        ({match.dimension_diff.thickness_diff_pct?.toFixed(1)}%)
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* File Info */}
            <div className="mt-4 text-sm text-gray-500">
              <FileText className="w-4 h-4 inline mr-1" />
              {match.file_name}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ScoreBar({ label, score, weight }) {
  const percentage = (score || 0) * 100

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">
          {label} <span className="text-gray-400">({(weight * 100).toFixed(0)}% weight)</span>
        </span>
        <span className={`font-medium ${getScoreColor(score)}`}>
          {percentage.toFixed(0)}%
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${
            percentage >= 80 ? 'bg-success-500' :
            percentage >= 50 ? 'bg-warning-500' : 'bg-danger-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}

function getScoreColor(score) {
  if (!score) return 'text-gray-500'
  if (score >= 0.8) return 'text-success-600'
  if (score >= 0.5) return 'text-warning-600'
  return 'text-danger-600'
}
